import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePos } from '../context/PosContext';
import {
  OnlineOrder,
  OnlineOrderChannel,
  OnlineOrderStatus,
  TransactionItem,
  Transaction,
  Product,
  KotTicket,
} from '../types';
import * as db from '../services/db';
import { computeBillTotals } from '../services/billing';
import { printReceiptAuto, printKotUsb } from '../services/receipt';
import { formatTime } from '../services/datetime';
import './Sections.css';
import './OnlineOrders.css';

// Online Orders board — a local-first reconstruction of online_order_layout.xml
// (status rail NEW/Preparing/Ready/Picked Up/Delivered/Cancelled + order list +
// detail pane) and item_online_order_view.xml (Accept & Save / Reject card).
// The Android app streams these from a cloud feed; here orders are entered
// manually (phone / WhatsApp / aggregator) and tracked through the pipeline,
// settling into a real sale on Accept so stock and reports stay in sync.

const CHANNELS: OnlineOrderChannel[] = ['Swiggy', 'Zomato', 'Phone', 'WhatsApp', 'Website', 'Other'];

// Pipeline stages shown on the left rail (plus an "All" pseudo-filter).
const STAGES: { key: OnlineOrderStatus; label: string }[] = [
  { key: 'new', label: 'New' },
  { key: 'preparing', label: 'Preparing' },
  { key: 'ready', label: 'Ready' },
  { key: 'picked_up', label: 'Picked Up' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'cancelled', label: 'Cancelled' },
];

// The next stage an order advances to, and the button label for it.
const NEXT: Partial<Record<OnlineOrderStatus, { to: OnlineOrderStatus; label: string }>> = {
  preparing: { to: 'ready', label: 'Mark Ready' },
  ready: { to: 'picked_up', label: 'Mark Picked Up' },
  picked_up: { to: 'delivered', label: 'Mark Delivered' },
};

const channelClass = (c: OnlineOrderChannel) => `oo-chip oo-chip-${c.toLowerCase()}`;

interface DraftLine extends TransactionItem {}

const OnlineOrders: React.FC = () => {
  const { products, sales, config, formatMoney, onlineOrders, addOnlineOrder, updateOnlineOrder, deleteOnlineOrder, recordSale, voidSale } = usePos();

  const [filter, setFilter] = useState<OnlineOrderStatus | 'all'>('new');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [presetChannel, setPresetChannel] = useState<OnlineOrderChannel>('Swiggy');

  // Deep link from the Swiggy & Zomato screen: /online-orders?new=Swiggy opens
  // the New Order form pre-set to that channel.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const want = searchParams.get('new');
    if (want && (CHANNELS as string[]).includes(want)) {
      setPresetChannel(want as OnlineOrderChannel);
      setShowForm(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // ---- counts for the status rail ----
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: onlineOrders.length };
    STAGES.forEach((s) => (c[s.key] = 0));
    onlineOrders.forEach((o) => (c[o.status] = (c[o.status] ?? 0) + 1));
    return c;
  }, [onlineOrders]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return onlineOrders
      .filter((o) => (filter === 'all' ? true : o.status === filter))
      .filter((o) =>
        !q
          ? true
          : [o.orderNo, o.customerName, o.customerPhone, o.channel].some((v) => (v ?? '').toLowerCase().includes(q))
      );
  }, [onlineOrders, filter, search]);

  const selected = onlineOrders.find((o) => o.id === selectedId) ?? null;

  // ---- order actions ----------------------------------------------------

  // Build a Transaction from an order and record it as a sale (decrements
  // stock, sequential bill no). COD → cash tender, Prepaid → digital.
  const settleToSale = async (o: OnlineOrder): Promise<Transaction> => {
    const totals = computeBillTotals({
      lines: o.items.map((i) => ({ total: i.total, taxRatePercent: i.taxRatePercent, cessPercent: i.cessPercent })),
      parcelCharges: o.deliveryCharge,
      roundOffEnabled: false,
    });
    const tx: Transaction = {
      id: db.newId(),
      billNo: '',
      customerName: o.customerName,
      items: o.items,
      subtotal: totals.billAmt,
      tax: totals.tax,
      cess: totals.cess,
      discount: 0,
      total: totals.finalTotal,
      orderType: 'parcel',
      parcelCharges: o.deliveryCharge || undefined,
      paymentMethod: o.paymentMode === 'COD' ? 'cash' : 'digital',
      udn: `${o.channel} #${o.orderNo}`,
      timestamp: new Date().toISOString(),
      status: 'completed',
    };
    return recordSale(tx);
  };

  const accept = async (o: OnlineOrder) => {
    if (o.items.length === 0) return alert('This order has no items to accept.');
    const saved = await settleToSale(o);
    updateOnlineOrder({ ...o, status: 'preparing', billNo: saved.billNo });
  };

  const reject = (o: OnlineOrder) => {
    if (!window.confirm(`Reject order ${o.orderNo}?`)) return;
    updateOnlineOrder({ ...o, status: 'cancelled' });
  };

  const advance = (o: OnlineOrder) => {
    const step = NEXT[o.status];
    if (step) updateOnlineOrder({ ...o, status: step.to });
  };

  // Cancel an already-accepted order: void its linked sale (restores stock).
  const cancelAccepted = (o: OnlineOrder) => {
    if (!window.confirm(`Cancel order ${o.orderNo}? Its bill will be voided.`)) return;
    if (o.billNo) {
      const sale = sales.find((s) => s.billNo === o.billNo);
      if (sale) voidSale(sale.id);
    }
    updateOnlineOrder({ ...o, status: 'cancelled' });
  };

  const remove = (o: OnlineOrder) => {
    if (!window.confirm(`Delete order ${o.orderNo} permanently?`)) return;
    deleteOnlineOrder(o.id);
    if (selectedId === o.id) setSelectedId(null);
  };

  const printBill = (o: OnlineOrder) => {
    const sale = o.billNo ? sales.find((s) => s.billNo === o.billNo) : undefined;
    if (!sale) return alert('No bill yet — accept the order first.');
    printReceiptAuto(sale, config);
  };

  const printKot = async (o: OnlineOrder) => {
    const kot: KotTicket = {
      id: o.id,
      kotNo: await db.peekNextKotNo(),
      tableName: `${o.channel} #${o.orderNo}`,
      customerName: o.customerName,
      lines: o.items.map((i) => ({ productId: i.productId, name: i.name, quantity: i.quantity, unitPrice: i.unitPrice })),
      amount: o.subtotal,
      status: 'active',
      timestamp: new Date().toISOString(),
    };
    const sent = await printKotUsb(kot, config.storeName);
    if (!sent) printKotFallback(kot, config.storeName);
  };

  return (
    <div className="oo-page">
      <div className="oo-topbar">
        <span className="oo-support">
          Support&nbsp;—&nbsp;Swiggy: 080-67466777 &nbsp;·&nbsp; Zomato: 9738383838
        </span>
        <button className="btn-teal oo-new-btn" onClick={() => setShowForm(true)}>+ New Online Order</button>
      </div>

      <div className="oo-body">
        {/* status rail */}
        <aside className="oo-rail">
          <button className={`oo-rail-btn ${filter === 'all' ? 'sel' : ''}`} onClick={() => setFilter('all')}>
            <span>ALL</span>
            <span className="oo-count">{counts.all}</span>
          </button>
          {STAGES.map((s) => (
            <button key={s.key} className={`oo-rail-btn ${filter === s.key ? 'sel' : ''}`} onClick={() => setFilter(s.key)}>
              <span>{s.label.toUpperCase()}</span>
              <span className="oo-count">{counts[s.key] ?? 0}</span>
            </button>
          ))}
        </aside>

        {/* order list */}
        <section className="oo-list">
          <input
            className="oo-search"
            placeholder="Search order no / customer / channel…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="oo-list-scroll">
            {visible.length === 0 && <p className="muted oo-empty">No orders in this view.</p>}
            {visible.map((o) => (
              <button
                key={o.id}
                className={`oo-card ${selectedId === o.id ? 'sel' : ''}`}
                onClick={() => setSelectedId(o.id)}
              >
                <div className="oo-card-top">
                  <span className={channelClass(o.channel)}>{o.channel}</span>
                  <span className={`badge oo-status oo-status-${o.status}`}>{statusLabel(o.status)}</span>
                </div>
                <div className="oo-card-no">#{o.orderNo}</div>
                <div className="oo-card-cust">{o.customerName || 'Walk-in / online'}</div>
                <div className="oo-card-foot">
                  <span className="muted">{o.items.length} item{o.items.length === 1 ? '' : 's'} · {formatTime(o.createdAt)}</span>
                  <span className="oo-card-total">{formatMoney(o.total)}</span>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* detail pane */}
        <section className="oo-detail">
          {!selected ? (
            <div className="oo-detail-empty muted">Select an order to view details.</div>
          ) : (
            <OrderDetail
              order={selected}
              formatMoney={formatMoney}
              onAccept={() => accept(selected)}
              onReject={() => reject(selected)}
              onAdvance={() => advance(selected)}
              onCancel={() => cancelAccepted(selected)}
              onDelete={() => remove(selected)}
              onPrintBill={() => printBill(selected)}
              onPrintKot={() => printKot(selected)}
            />
          )}
        </section>
      </div>

      {showForm && (
        <NewOrderModal
          products={products}
          defaultTaxRate={config.taxRatePercent}
          initialChannel={presetChannel}
          onClose={() => setShowForm(false)}
          onSave={(draft) => {
            const created = addOnlineOrder(draft);
            setShowForm(false);
            setFilter('new');
            setSelectedId(created.id);
          }}
        />
      )}
    </div>
  );
};

// ---- detail pane --------------------------------------------------------

interface DetailProps {
  order: OnlineOrder;
  formatMoney: (n: number) => string;
  onAccept: () => void;
  onReject: () => void;
  onAdvance: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onPrintBill: () => void;
  onPrintKot: () => void;
}

const OrderDetail: React.FC<DetailProps> = ({ order: o, formatMoney, onAccept, onReject, onAdvance, onCancel, onDelete, onPrintBill, onPrintKot }) => {
  const step = NEXT[o.status];
  return (
    <div className="oo-detail-inner">
      <div className="oo-d-head">
        <div>
          <span className={channelClass(o.channel)}>{o.channel}</span>
          <span className="oo-d-no">#{o.orderNo}</span>
        </div>
        <span className={`badge oo-status oo-status-${o.status}`}>{statusLabel(o.status)}</span>
      </div>

      {o.otp && <div className="oo-d-otp">OTP: <strong>{o.otp}</strong></div>}
      {o.instructions && <div className="oo-d-note">📝 {o.instructions}</div>}

      <div className="oo-d-card">
        <div className="oo-d-card-title">Customer Details</div>
        <div className="oo-d-cust-name">{o.customerName || '—'} {o.customerPhone && <span className="muted">· {o.customerPhone}</span>}</div>
        {o.address && <div className="muted">{o.address}</div>}
        <div className="oo-d-pay">Payment: <strong>{o.paymentMode}</strong></div>
      </div>

      {(o.riderName || o.riderPhone) && (
        <div className="oo-d-rider">🛵 {o.riderName || 'Rider'}{o.riderPhone ? ` · ${o.riderPhone}` : ''}</div>
      )}

      <table className="section-table oo-d-items">
        <thead>
          <tr><th>Item Name</th><th className="right">Qty</th><th className="right">Price</th><th className="right">Amt</th></tr>
        </thead>
        <tbody>
          {o.items.map((i, idx) => (
            <tr key={idx}>
              <td>{i.name}</td>
              <td className="right">{i.quantity}</td>
              <td className="right">{formatMoney(i.unitPrice)}</td>
              <td className="right">{formatMoney(i.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="oo-d-totals">
        <div className="row"><span>Sub Total</span><span>{formatMoney(o.subtotal)}</span></div>
        {!!o.tax && <div className="row"><span>Tax</span><span>{formatMoney(o.tax)}</span></div>}
        {!!o.deliveryCharge && <div className="row"><span>Delivery</span><span>{formatMoney(o.deliveryCharge)}</span></div>}
        <div className="row oo-d-net"><span>NET AMT</span><span>{formatMoney(o.total)}</span></div>
      </div>

      {o.billNo && <div className="oo-d-billno muted">Bill No: {o.billNo}</div>}

      <div className="oo-d-actions">
        {o.status === 'new' && (
          <>
            <button className="btn-red" onClick={onReject}>Reject</button>
            <button className="btn-teal oo-accept" onClick={onAccept}>Accept &amp; Save</button>
          </>
        )}
        {step && (
          <button className="oo-advance" onClick={onAdvance}>{step.label}</button>
        )}
        {o.billNo && o.status !== 'cancelled' && (
          <>
            <button className="btn-grey" onClick={onPrintKot}>Print KOT</button>
            <button className="btn-grey" onClick={onPrintBill}>Print Bill</button>
          </>
        )}
        {o.status !== 'new' && o.status !== 'cancelled' && o.status !== 'delivered' && (
          <button className="btn-red" onClick={onCancel}>Cancel Order</button>
        )}
        {(o.status === 'cancelled' || o.status === 'delivered') && (
          <button className="btn-grey" onClick={onDelete}>Delete</button>
        )}
      </div>
    </div>
  );
};

// ---- new-order modal ----------------------------------------------------

interface NewOrderProps {
  products: Product[];
  defaultTaxRate: number;
  initialChannel?: OnlineOrderChannel;
  onClose: () => void;
  onSave: (draft: Omit<OnlineOrder, 'id' | 'createdAt' | 'updatedAt'>) => void;
}

const NewOrderModal: React.FC<NewOrderProps> = ({ products, defaultTaxRate, initialChannel, onClose, onSave }) => {
  const [channel, setChannel] = useState<OnlineOrderChannel>(initialChannel ?? 'Swiggy');
  const [orderNo, setOrderNo] = useState(String(db.nextOnlineOrderNo()));
  const [paymentMode, setPaymentMode] = useState<'Prepaid' | 'COD'>('Prepaid');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [address, setAddress] = useState('');
  const [otp, setOtp] = useState('');
  const [riderName, setRiderName] = useState('');
  const [riderPhone, setRiderPhone] = useState('');
  const [instructions, setInstructions] = useState('');
  const [deliveryCharge, setDeliveryCharge] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [psearch, setPsearch] = useState('');

  const results = useMemo(() => {
    const q = psearch.trim().toLowerCase();
    if (!q) return [];
    return products
      .filter((p) => p.name.toLowerCase().includes(q) || (p.barcode ?? '').includes(q))
      .slice(0, 8);
  }, [psearch, products]);

  const addProduct = (p: Product) => {
    setLines((prev) => {
      const existing = prev.find((l) => l.productId === p.id);
      if (existing) {
        return prev.map((l) =>
          l.productId === p.id ? { ...l, quantity: l.quantity + 1, total: (l.quantity + 1) * l.unitPrice } : l
        );
      }
      const price = p.price;
      return [
        ...prev,
        {
          productId: p.id,
          name: p.name,
          quantity: 1,
          unitPrice: price,
          taxRatePercent: p.taxRatePercent ?? defaultTaxRate,
          cessPercent: p.cessPercent,
          total: price,
        },
      ];
    });
    setPsearch('');
  };

  const setQty = (productId: string, qty: number) => {
    if (qty <= 0) return setLines((prev) => prev.filter((l) => l.productId !== productId));
    setLines((prev) => prev.map((l) => (l.productId === productId ? { ...l, quantity: qty, total: qty * l.unitPrice } : l)));
  };

  const setPrice = (productId: string, price: number) => {
    setLines((prev) => prev.map((l) => (l.productId === productId ? { ...l, unitPrice: price, total: l.quantity * price } : l)));
  };

  const delivery = parseFloat(deliveryCharge) || 0;
  const totals = useMemo(
    () =>
      computeBillTotals({
        lines: lines.map((l) => ({ total: l.total, taxRatePercent: l.taxRatePercent, cessPercent: l.cessPercent })),
        parcelCharges: delivery,
        roundOffEnabled: false,
      }),
    [lines, delivery]
  );

  const save = () => {
    if (!orderNo.trim()) return alert('Order number is required.');
    if (lines.length === 0) return alert('Add at least one item.');
    onSave({
      orderNo: orderNo.trim(),
      channel,
      status: 'new',
      customerName: customerName.trim() || undefined,
      customerPhone: customerPhone.trim() || undefined,
      address: address.trim() || undefined,
      paymentMode,
      otp: otp.trim() || undefined,
      riderName: riderName.trim() || undefined,
      riderPhone: riderPhone.trim() || undefined,
      instructions: instructions.trim() || undefined,
      items: lines,
      deliveryCharge: delivery || undefined,
      subtotal: totals.billAmt,
      tax: totals.tax,
      total: totals.finalTotal,
    });
  };

  return (
    <div className="oo-modal-backdrop" onClick={onClose}>
      <div className="oo-modal" onClick={(e) => e.stopPropagation()}>
        <div className="oo-modal-head">
          <h2>New Online Order</h2>
          <button className="oo-x" onClick={onClose}>✕</button>
        </div>

        <div className="oo-modal-body">
          <div className="oo-form-grid">
            <label>Channel
              <select value={channel} onChange={(e) => setChannel(e.target.value as OnlineOrderChannel)}>
                {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label>Order No
              <input value={orderNo} onChange={(e) => setOrderNo(e.target.value)} />
            </label>
            <label>Payment
              <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value as 'Prepaid' | 'COD')}>
                <option value="Prepaid">Prepaid (paid online)</option>
                <option value="COD">COD (collect on delivery)</option>
              </select>
            </label>
            <label>Customer Name
              <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
            </label>
            <label>Mobile
              <input value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} />
            </label>
            <label>OTP
              <input value={otp} onChange={(e) => setOtp(e.target.value)} />
            </label>
            <label className="oo-span2">Delivery Address
              <input value={address} onChange={(e) => setAddress(e.target.value)} />
            </label>
            <label>Rider Name
              <input value={riderName} onChange={(e) => setRiderName(e.target.value)} />
            </label>
            <label>Rider Mobile
              <input value={riderPhone} onChange={(e) => setRiderPhone(e.target.value)} />
            </label>
            <label className="oo-span2">Instructions
              <input value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="e.g. less spicy, no onion" />
            </label>
          </div>

          {/* item picker */}
          <div className="oo-item-picker">
            <input
              className="oo-search"
              placeholder="Search product to add…"
              value={psearch}
              onChange={(e) => setPsearch(e.target.value)}
            />
            {results.length > 0 && (
              <ul className="search-results">
                {results.map((p) => (
                  <li key={p.id} onClick={() => addProduct(p)}>
                    <span>{p.name}</span>
                    <span className="res-price">{p.price.toFixed(2)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <table className="section-table oo-line-table">
            <thead>
              <tr><th>Item</th><th className="right">Qty</th><th className="right">Price</th><th className="right">Total</th><th></th></tr>
            </thead>
            <tbody>
              {lines.length === 0 && (
                <tr><td colSpan={5} className="muted">No items yet — search above to add.</td></tr>
              )}
              {lines.map((l) => (
                <tr key={l.productId}>
                  <td>{l.name}</td>
                  <td className="right">
                    <button className="qty-btn" onClick={() => setQty(l.productId, l.quantity - 1)}>−</button>
                    <input
                      className="oo-qty-in"
                      type="number"
                      value={l.quantity}
                      onChange={(e) => setQty(l.productId, parseFloat(e.target.value) || 0)}
                    />
                    <button className="qty-btn" onClick={() => setQty(l.productId, l.quantity + 1)}>＋</button>
                  </td>
                  <td className="right">
                    <input
                      className="oo-price-in"
                      type="number"
                      value={l.unitPrice}
                      onChange={(e) => setPrice(l.productId, parseFloat(e.target.value) || 0)}
                    />
                  </td>
                  <td className="right">{l.total.toFixed(2)}</td>
                  <td className="right"><button className="row-del" onClick={() => setQty(l.productId, 0)}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="oo-form-foot">
            <label className="oo-delivery">Delivery Charge
              <input type="number" value={deliveryCharge} onChange={(e) => setDeliveryCharge(e.target.value)} placeholder="0.00" />
            </label>
            <div className="oo-foot-totals">
              <div><span className="muted">Sub Total</span> {totals.billAmt.toFixed(2)}</div>
              <div><span className="muted">Tax</span> {totals.tax.toFixed(2)}</div>
              <div className="oo-foot-net">NET {totals.finalTotal.toFixed(2)}</div>
            </div>
          </div>
        </div>

        <div className="oo-modal-actions">
          <button className="btn-grey" onClick={onClose}>Cancel</button>
          <button className="btn-teal" onClick={save}>Save Order</button>
        </div>
      </div>
    </div>
  );
};

// ---- helpers ------------------------------------------------------------

function statusLabel(s: OnlineOrderStatus): string {
  return (STAGES.find((x) => x.key === s)?.label ?? s);
}

// Browser-print fallback for a KOT when no USB printer is configured.
function printKotFallback(kot: KotTicket, storeName: string): void {
  const rows = kot.lines
    .map((l) => `<tr><td>${escapeHtml(l.name)}</td><td style="text-align:right">${l.quantity}</td></tr>`)
    .join('');
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>KOT</title>
    <style>@page{size:80mm auto;margin:0}body{font-family:'Courier New',monospace;font-size:13px;margin:0;padding:6px 8px}
    .ctr{text-align:center}.hr{border-top:1px dashed #000;margin:4px 0}table{width:100%}td{padding:1px 0}</style></head>
    <body onload="window.print()">
      <div class="ctr"><strong>${escapeHtml(storeName)}</strong></div>
      <div class="ctr">KITCHEN ORDER TICKET</div>
      <div class="ctr">${escapeHtml(kot.tableName)}</div>
      ${kot.customerName ? `<div class="ctr">${escapeHtml(kot.customerName)}</div>` : ''}
      <div class="hr"></div>
      <table><thead><tr><td>ITEM</td><td style="text-align:right">QTY</td></tr></thead><tbody>${rows}</tbody></table>
      <div class="hr"></div>
    </body></html>`;
  const w = window.open('', '_blank', 'width=360,height=600');
  if (!w) return alert('Allow pop-ups to print the KOT.');
  w.document.open();
  w.document.write(html);
  w.document.close();
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export default OnlineOrders;
